import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { updateCart, setCart } from "../redux/cart";
import {
  getCart,
  increaseSkuQuantity,
  decreaseSkuQuantity,
  increaseBundleQuantity,
  decreaseBundleQuantity,
} from "../services/cart";
import { cartDataMapper } from "../helpers/dataMapper";
import { APP_PATHS } from "../constants/paths";

export const useCart = (setCartState) => {
  const [isLoading, setIsLoading] = useState(false);
  const { pathname: currentPath } = useLocation();
  const dispatch = useDispatch();

  const skuQuantityIncreasedHandler = async (
    skuId,
    price,
    currentQuantity,
    increaseBy
  ) => {
    try {
      setIsLoading(true);
      const response = await increaseSkuQuantity(
        skuId,
        currentQuantity,
        price,
        increaseBy
      );
      const {
        data: {
          data: {
            cartTotals: { cartSize },
          },
        },
      } = response;
      if (currentPath !== APP_PATHS.CART.url) {
        dispatch(
          updateCart({
            skus: [{ id: skuId, quantity: increaseBy }],
            bundles: [],
            cartSize,
          })
        );
      } else {
        const response = await getCart();
        const { cart } = response.data;
        const { skuList, bundleList, cartSize } = cart;
        setCartState(cart);
        dispatch(setCart(cartDataMapper(skuList, bundleList, cartSize)));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const skuQuantityDecreasedHandler = async (
    skuId,
    price,
    currentQuantity,
    decreaseBy
  ) => {
    try {
      setIsLoading(true);
      const response = await decreaseSkuQuantity(
        skuId,
        currentQuantity,
        price,
        decreaseBy
      );
      const {
        data: {
          data: {
            cartTotals: { cartSize },
          },
        },
      } = response;
      if (currentPath !== APP_PATHS.CART.url) {
        dispatch(
          updateCart({
            skus: [{ id: skuId, quantity: -decreaseBy }],
            bundles: [],
            cartSize,
          })
        );
      } else {
        const response = await getCart();
        const { cart } = response.data;
        const { skuList, bundleList, cartSize } = cart;
        setCartState(cart);
        dispatch(setCart(cartDataMapper(skuList, bundleList, cartSize)));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  /*
   * @param {array} bundleSkus [{ id:{number}, quantity:{number} }]
   */
  const bundleQuantityIncreasedHandler = async (
    bundleId,
    currentQuantity,
    increaseBy,
    bundleSkus
  ) => {
    try {
      setIsLoading(true);
      const response = await increaseBundleQuantity(
        bundleId,
        currentQuantity,
        increaseBy
      );
      const {
        canAddMore,
        cartTotals: { cartSize },
      } = response.data.data;
      if (currentPath !== APP_PATHS.CART.url) {
        dispatch(
          updateCart({
            skus: [],
            bundles: [
              {
                id: bundleId,
                quantity: increaseBy,
                canAddMore,
                skus: bundleSkus,
              },
            ],
            cartSize,
          })
        );
      } else {
        const response = await getCart();
        const { cart } = response.data;
        const { skuList, bundleList, cartSize } = cart;
        setCartState(cart);
        dispatch(setCart(cartDataMapper(skuList, bundleList, cartSize)));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  /*
   * @param {array} bundleSkus [{ id:{number}, quantity:{number} }]
   */
  const bundleQuantityDecreasedHandler = async (
    bundleId,
    currentQuantity,
    decreaseBy,
    bundleSkus
  ) => {
    try {
      setIsLoading(true);
      const response = await decreaseBundleQuantity(
        bundleId,
        currentQuantity,
        decreaseBy
      );
      const {
        cartTotals: { cartSize },
      } = response.data.data;
      if (currentPath !== APP_PATHS.CART.url) {
        dispatch(
          updateCart({
            skus: [],
            bundles: [
              {
                id: bundleId,
                quantity: -decreaseBy,
                canAddMore: true, // consider all decrease bundle quantity with canAddMore with true
                skus: bundleSkus,
              },
            ],
            cartSize,
          })
        );
      } else {
        const response = await getCart();
        const { cart } = response.data;
        const { skuList, bundleList, cartSize } = cart;
        setCartState(cart);
        dispatch(setCart(cartDataMapper(skuList, bundleList, cartSize)));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    skuQuantityIncreasedHandler,
    skuQuantityDecreasedHandler,
    bundleQuantityIncreasedHandler,
    bundleQuantityDecreasedHandler,
    isLoading,
  };
};
